import React, { useState } from "react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";
import { toast } from "sonner";
import { Bookmark, BookmarkPlus, Trash2, Users, Star, ArrowUp, ArrowDown, RefreshCw } from "lucide-react";

// Power BI-style bookmarks: captures filters + slicers + filter widgets + what-if,
// supports default view, ordering, sharing and overwrite-with-current-state.
export function BookmarkMenu({ dashboardId, gfVals, slicerVals, fwVals, whatIfVals, onApply }) {
  const [items, setItems] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState("");
  const [shared, setShared] = useState(false);
  const [isDefault, setIsDefault] = useState(false);

  const load = () => api.get(`/dashboards/${dashboardId}/bookmarks`).then(({ data }) => setItems(data)).catch(() => {});

  const currentState = () => ({ gf_vals: gfVals || {}, slicer_vals: slicerVals || {}, fw_vals: fwVals || {}, what_if: whatIfVals || {} });

  const save = async () => {
    if (!name.trim()) { toast.error("Name the bookmark"); return; }
    try {
      await api.post(`/dashboards/${dashboardId}/bookmarks`, { name: name.trim(), ...currentState(), shared, is_default: isDefault });
      toast.success(isDefault ? "Bookmark saved as your default view" : shared ? "Bookmark saved & shared with the team" : "Bookmark saved");
      setSaveOpen(false); setName(""); setShared(false); setIsDefault(false);
    } catch { toast.error("Failed to save bookmark"); }
  };
  const del = async (e, id) => {
    e.stopPropagation();
    try { await api.delete(`/dashboards/${dashboardId}/bookmarks/${id}`); load(); } catch {}
  };
  const toggleShare = async (e, b) => {
    e.stopPropagation();
    try {
      await api.patch(`/dashboards/${dashboardId}/bookmarks/${b.id}`, { shared: !b.shared });
      toast.success(b.shared ? "Bookmark unshared" : "Bookmark shared with the team");
      load();
    } catch { toast.error("Only the bookmark owner can share/unshare"); }
  };
  const toggleDefault = async (e, b) => {
    e.stopPropagation();
    try {
      await api.patch(`/dashboards/${dashboardId}/bookmarks/${b.id}`, { is_default: !b.is_default });
      toast.success(b.is_default ? "Default view removed" : `"${b.name}" is now your default view`);
      load();
    } catch { toast.error("Only the bookmark owner can set a default"); }
  };
  const overwrite = async (e, b) => {
    e.stopPropagation();
    try {
      await api.patch(`/dashboards/${dashboardId}/bookmarks/${b.id}`, { state: currentState() });
      toast.success(`"${b.name}" updated with the current view`);
      load();
    } catch { toast.error("Only the bookmark owner can update it"); }
  };
  const move = async (e, b, dir) => {
    e.stopPropagation();
    const mine = items.filter((x) => x.user_id === b.user_id);
    const idx = mine.findIndex((x) => x.id === b.id);
    const swap = mine[idx + dir];
    if (!swap) return;
    try {
      await Promise.all([
        api.patch(`/dashboards/${dashboardId}/bookmarks/${b.id}`, { order: swap.order ?? idx + dir }),
        api.patch(`/dashboards/${dashboardId}/bookmarks/${swap.id}`, { order: b.order ?? idx }),
      ]);
      load();
    } catch { /* not owner */ }
  };

  return (
    <>
      <DropdownMenu onOpenChange={(v) => v && load()}>
        <DropdownMenuTrigger asChild>
          <Button data-testid="bookmarks-btn" variant="outline" className="bg-[color:var(--surface)]" title="Bookmarks (saved views)"><Bookmark className="w-4 h-4" /></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuItem data-testid="bookmark-save" onClick={() => setSaveOpen(true)}>
            <BookmarkPlus className="w-4 h-4 mr-2 text-[color:var(--brand-ocean)]" />Save current view…
          </DropdownMenuItem>
          {items.length > 0 && <DropdownMenuSeparator />}
          {items.map((b) => (
            <DropdownMenuItem key={b.id} data-testid="bookmark-item" onClick={() => { onApply(b); toast.success(`Applied "${b.name}"`); }}>
              {b.is_default
                ? <Star className="w-4 h-4 mr-2 text-[#F59E0B] fill-[#F59E0B]" title="Default view" />
                : b.shared
                  ? <Users className="w-4 h-4 mr-2 text-[color:var(--brand-mint)]" title={`Shared by ${b.user_name || "a teammate"}`} />
                  : <Bookmark className="w-4 h-4 mr-2 text-[color:var(--ink-3)]" />}
              <span className="flex-1 truncate">{b.name}{b.shared && b.user_name ? <span className="text-[10px] text-[color:var(--ink-3)]"> · {b.user_name}</span> : null}</span>
              <span className="flex items-center gap-1 ml-1.5">
                <button data-testid="bookmark-move-up" title="Move up" onClick={(e) => move(e, b, -1)} className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"><ArrowUp className="w-3 h-3" /></button>
                <button data-testid="bookmark-move-down" title="Move down" onClick={(e) => move(e, b, 1)} className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"><ArrowDown className="w-3 h-3" /></button>
                <button data-testid="bookmark-overwrite" title="Update with current view" onClick={(e) => overwrite(e, b)} className="text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]"><RefreshCw className="w-3 h-3" /></button>
                <button data-testid="bookmark-default-toggle" title={b.is_default ? "Unset default" : "Set as default view"} onClick={(e) => toggleDefault(e, b)} className="text-[color:var(--ink-3)] hover:text-[#F59E0B]"><Star className="w-3.5 h-3.5" /></button>
                <button data-testid="bookmark-share-toggle" title={b.shared ? "Unshare" : "Share with team"} onClick={(e) => toggleShare(e, b)} className="text-[color:var(--ink-3)] hover:text-[color:var(--brand-mint)]"><Users className="w-3.5 h-3.5" /></button>
                <button onClick={(e) => del(e, b.id)} title="Delete" className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><Trash2 className="w-3.5 h-3.5" /></button>
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><BookmarkPlus className="w-5 h-5 text-[color:var(--brand-ocean)]" />Save bookmark</DialogTitle></DialogHeader>
          <div className="py-1 space-y-3">
            <div><Label>Name</Label><Input data-testid="bookmark-name" className="mt-1.5" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. West · Electronics · Last 30d" /></div>
            <p className="text-[11px] text-[color:var(--ink-3)]">Captures filters, slicers, filter widgets and what-if values.</p>
            <label className="flex items-center gap-2 text-sm text-[color:var(--ink-2)]">
              <Switch data-testid="bookmark-shared-toggle" checked={shared} onCheckedChange={setShared} />
              <Users className="w-4 h-4 text-[color:var(--brand-mint)]" />Share with everyone on this dashboard
            </label>
            <label className="flex items-center gap-2 text-sm text-[color:var(--ink-2)]">
              <Switch data-testid="bookmark-default-switch" checked={isDefault} onCheckedChange={setIsDefault} />
              <Star className="w-4 h-4 text-[#F59E0B]" />Apply automatically when I open this dashboard
            </label>
          </div>
          <DialogFooter><Button data-testid="bookmark-save-confirm" onClick={save} className="bg-[color:var(--brand-ocean)] text-white">Save bookmark</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
